import { LitElement, unsafeCSS, type CSSResult } from 'lit';
import hostCss from './host.css';

const idNamespace = Math.random().toString(36).slice(2);
let nextId = 0;

export function createUniqueId(prefix: string): string {
    return `${prefix}-${idNamespace}-${++nextId}`;
}

/**
 * Base class of the lib-* elements of WAGO.SolutionsPlatform.UILibrary.Core.
 *
 * Provides
 *  - the shared shadow DOM host style (hostCss), to be listed first in the `styles`
 *    of every derived element,
 *  - a helper to dispatch composed, bubbling custom events towards Blazor wrappers or
 *    plain JavaScript consumers.
 */
export abstract class LibElement extends LitElement {
    static override readonly shadowRootOptions: ShadowRootInit = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    };

    static override get observedAttributes(): string[] {
        return [...super.observedAttributes, 'tabindex'];
    }

    /** Selects this component's own keyboard entry points; containers do not opt in. */
    protected get hostFocusSelector(): string { return ''; }

    private consumerTabIndex = false;
    private writingTabIndex = false;
    private managedTabIndex = false;
    private readonly focusObserver = new MutationObserver(() => this.syncHostTabIndex());
    private readonly focusSlotChanged = () => this.requestUpdate();

    constructor() {
        super();
        this.addController({
            hostUpdated: () => {
                this.renderRoot.addEventListener('slotchange', this.focusSlotChanged);
                if (this.hostFocusSelector) {
                    this.focusObserver.observe(this.renderRoot, {
                        subtree: true, childList: true, attributes: true,
                        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'hidden', 'inert'],
                    });
                } else {
                    this.focusObserver.disconnect();
                }
                this.syncHostTabIndex();
            },
            hostConnected: () => { if (this.hasUpdated) this.requestUpdate(); },
            hostDisconnected: () => {
                this.focusObserver.disconnect();
                this.renderRoot.removeEventListener('slotchange', this.focusSlotChanged);
            },
        });
    }

    override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
        super.attributeChangedCallback(name, previous, value);
        if (name === 'tabindex' && !this.writingTabIndex) {
            this.consumerTabIndex = value !== null;
            this.managedTabIndex = false;
            this.requestUpdate();
        }
    }

    private syncHostTabIndex(): void {
        if (!this.isConnected || this.consumerTabIndex) return;
        if (!this.hostFocusSelector) {
            if (this.managedTabIndex) {
                this.writingTabIndex = true;
                try {
                    this.removeAttribute('tabindex');
                    this.managedTabIndex = false;
                } finally {
                    this.writingTabIndex = false;
                }
            }
            return;
        }
        const available = Array.from(this.renderRoot.querySelectorAll<HTMLElement>(this.hostFocusSelector))
            .some(element => element.tabIndex >= 0 && !element.matches(':disabled')
                && element.getAttribute('aria-disabled') !== 'true'
                && !element.closest('[hidden], [inert]'));
        const next = available ? 0 : -1;
        if (this.getAttribute('tabindex') === String(next)) return;
        this.writingTabIndex = true;
        try {
            this.tabIndex = next;
            this.managedTabIndex = true;
        } finally {
            this.writingTabIndex = false;
        }
    }

    /** The shared host style every lib-* element adopts ahead of its own styles. */
    protected static readonly hostStyles: CSSResult = unsafeCSS(hostCss);

    /**
     * Dispatches a custom event that crosses the shadow boundary and bubbles up the tree.
     * @param type The event type, namespaced like `lib-change` to avoid collisions.
     * @param detail Optional event payload carried in `event.detail`.
     */
    protected emit(type: string, detail?: unknown): void {
        this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
    }
}
